import EmojiEventsIcon from '@material-design-icons/svg/outlined/emoji_events.svg?react';
import LaunchIcon from '@material-design-icons/svg/outlined/launch.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { useEffect, useMemo, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { useTRPC } from '../../hooks/useTRPC';
import { QUEST_COMPLETION_API_URL } from '../../lib/config';
import { cn } from '../../lib/utils';
import { captureAndReportError } from '../../utils/errorReporting';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import {
  deriveRoundDateState,
  statusDotToneClass,
  type RoundDateState,
  type StatusTone,
} from '../../pages/quests/questWorkspace';
import { useOptionalDappContext } from '../DappLayout';
import { CopyableValue } from '../common/detailParts';
import { Button } from '../ui/button';
import { DashboardHeader } from './DashboardHeader';

type QuestTier = 'Easy' | 'Deep';
type QuestStatus = 'Draft' | 'Active' | 'Disabled' | 'Archived';

interface DappQuest {
  id: string;
  questKey: string;
  tier: QuestTier;
  status: QuestStatus;
  title: string;
  description: string;
  completionCriteria: string;
  tips: string[];
  deepLinkUrl: string | null;
  badgeTitle: string;
  badgeImageUrl: string | null;
  sortOrder: number;
  completionCount: number;
}

interface DappQuestRound {
  roundAppId: string;
  roundId: string;
  seasonId: string;
  seasonNumber: number;
  roundNumber: number;
  status: string;
  startsAt: string;
  endsAt: string;
  eligibilityStartsAt: string;
  eligibilityEndsAt: string;
  revealStartsAt: string | null;
  revealEndsAt: string | null;
  appName: string;
  androidPackage: string;
  curationNote: string;
  iconUrl: string | null;
  isUnrevealed: boolean;
  quests: DappQuest[];
}

interface DappQuestsData {
  dappId: string;
  dappName: string;
  androidPackage: string | null;
  rounds: DappQuestRound[];
}

const CARD_CLASS =
  'rounded-2xl border border-white/[0.06] bg-black/35 shadow-[0_0.375px_0.375px_0_inset_rgba(255,255,255,0.17)]';

const ROUND_STATE_TONE: Record<RoundDateState, StatusTone> = {
  Unknown: 'neutral',
  Scheduled: 'info',
  Open: 'success',
  Finalizing: 'warn',
  Closed: 'neutral',
  Expired: 'warn',
};

const ROUND_STATE_LABEL: Record<RoundDateState, string> = {
  Unknown: 'Dates TBD',
  Scheduled: 'Upcoming',
  Open: 'Live',
  Finalizing: 'Finalizing',
  Closed: 'Ended',
  Expired: 'Expired',
};

export const DashboardQuests = () => {
  const dappId = useOptionalDappContext()?.dappId;
  const { canRender, isChecking } = useRequireAuth();
  const { dappData } = useDappDataOptional();
  const trpc = useTRPC();
  const [data, setData] = useState<DappQuestsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const dappName = data?.dappName || dappData?.dappName || null;

  usePageTitle(
    dappId && dappName
      ? `${dappName} - Quests | Publishing Portal`
      : 'Quests | Publishing Portal',
  );

  useEffect(() => {
    if (!canRender || !dappId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    trpc.getDappQuests
      .query({ dappId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result._tag === 'Left') {
          setError(result.left.message);
          setData(null);
          return;
        }
        setData(result.right);
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        captureAndReportError(loadError, {
          component: 'DashboardQuests',
          operation: 'getDappQuests',
          dappId,
        });
        setError('Failed to load quests');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canRender, dappId, trpc, refreshKey]);

  const rounds = useMemo(() => {
    const sorted = [...(data?.rounds ?? [])];
    sorted.sort(
      (a, b) =>
        a.seasonNumber - b.seasonNumber || a.roundNumber - b.roundNumber,
    );
    return sorted;
  }, [data]);

  if (isChecking || !canRender) {
    return null;
  }

  const isInitialLoad = isLoading && !data;
  const hasContent = Boolean(data && rounds.length > 0);

  return (
    <div className="flex h-full flex-col">
      <DashboardHeader
        icon={<EmojiEventsIcon className="size-4 fill-white" />}
        title="Quests"
        actions={
          dappId && data ? (
            <Button
              variant="outline"
              onClick={() => setRefreshKey((key) => key + 1)}
              disabled={isLoading}
              className="h-8 gap-2 border-neutral-700 bg-neutral-950 px-3 text-xs text-neutral-200 hover:bg-neutral-900"
            >
              <RefreshIcon
                className={cn(
                  'size-4 fill-current',
                  isLoading && 'animate-spin',
                )}
              />
              Refresh
            </Button>
          ) : undefined
        }
      />

      <div className="flex-1 overflow-auto rounded-b-xl bg-[#161717]">
        <div className="mx-auto w-full max-w-3xl px-5 py-10 lg:px-6">
          {!dappId ? (
            <EmptyState title="No dApp selected" />
          ) : isInitialLoad ? (
            <QuestsSkeleton />
          ) : error ? (
            <ErrorState
              message={error}
              onRetry={() => setRefreshKey((k) => k + 1)}
            />
          ) : !hasContent || !data ? (
            <EmptyState
              title="No quests yet"
              description="This app isn’t part of any quest rounds right now. Quests appear here once the Solana Mobile team adds it to a challenge season."
            />
          ) : (
            <div className="flex flex-col gap-8">
              {rounds.map((round) => (
                <RoundSection key={round.roundAppId} round={round} />
              ))}
              <CompletionApiCard />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function RoundSection({ round }: { round: DappQuestRound }) {
  const state = deriveRoundDateState(
    {
      startsAt: round.startsAt,
      endsAt: round.endsAt,
      revealStartsAt: round.revealStartsAt ?? '',
      revealEndsAt: round.revealEndsAt ?? '',
    },
    new Date(),
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
        <h2 className="text-base font-semibold text-white">
          Round {round.roundNumber}
        </h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-400">
          <span
            className={cn(
              'size-1.5 rounded-full',
              statusDotToneClass[ROUND_STATE_TONE[state]],
            )}
            aria-hidden="true"
          />
          {ROUND_STATE_LABEL[state]}
        </span>
        <span className="ml-auto text-xs tabular-nums text-neutral-500">
          Season {round.seasonNumber} ·{' '}
          {formatDateRange(round.startsAt, round.endsAt)}
        </span>
      </div>

      <div className={CARD_CLASS}>
        <div className="divide-y divide-white/[0.06]">
          {round.quests.map((quest) => (
            <QuestRow key={quest.id} quest={quest} />
          ))}
        </div>
      </div>
    </section>
  );
}

function QuestRow({ quest }: { quest: DappQuest }) {
  return (
    <div className="flex gap-4 p-5">
      <QuestBadgeArt
        imageUrl={quest.badgeImageUrl}
        title={quest.badgeTitle}
        tier={quest.tier}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="truncate text-sm font-medium text-white">
            {quest.title}
          </h3>
          <span className="shrink-0 text-xs text-neutral-500">
            {quest.tier}
            {quest.status !== 'Active' ? ` · ${quest.status}` : ''}
          </span>
        </div>

        {quest.description && (
          <p className="mt-1 text-sm leading-relaxed text-neutral-400">
            {quest.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-neutral-500">
          <CopyableValue
            value={quest.questKey}
            display={
              <span className="font-mono text-xs text-neutral-400">
                {quest.questKey}
              </span>
            }
            truncate={false}
          />
          <span>
            {quest.completionCount.toLocaleString()}{' '}
            {quest.completionCount === 1 ? 'completion' : 'completions'}
          </span>
          {quest.deepLinkUrl && (
            <a
              href={quest.deepLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-neutral-400 hover:text-white"
            >
              <LaunchIcon className="size-3.5 fill-current" />
              Open
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestBadgeArt({
  imageUrl,
  title,
  tier,
}: {
  imageUrl: string | null;
  title: string;
  tier: QuestTier;
}) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        className="size-10 shrink-0 rounded-lg object-cover ring-1 ring-inset ring-white/10"
      />
    );
  }

  return (
    <div
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset',
        tier === 'Deep'
          ? 'bg-blue-500/10 ring-blue-500/20'
          : 'bg-neutral-800/70 ring-white/10',
      )}
      aria-hidden="true"
      title={title}
    >
      <EmojiEventsIcon
        className={cn(
          'size-5 fill-current',
          tier === 'Deep' ? 'text-blue-300' : 'text-neutral-400',
        )}
      />
    </div>
  );
}

function CompletionApiCard() {
  return (
    <section className={cn(CARD_CLASS, 'p-5')}>
      <div className="flex items-center gap-2 text-sm font-medium text-white">
        <LaunchIcon className="size-4 fill-current" />
        Reporting completions
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">
        From your backend, POST to this endpoint with your{' '}
        <code className="font-mono text-neutral-400">X-API-Key</code> and a
        quest’s <code className="font-mono text-neutral-400">quest_id</code>{' '}
        (shown above).
      </p>

      <div className="mt-3 flex items-center gap-2 rounded-lg bg-neutral-950/70 px-3 py-2">
        <span className="shrink-0 font-mono text-[11px] font-semibold text-emerald-400">
          POST
        </span>
        <CopyableValue
          value={QUEST_COMPLETION_API_URL}
          display={
            <span className="font-mono text-xs text-neutral-300">
              {QUEST_COMPLETION_API_URL}
            </span>
          }
          truncate
          className="min-w-0 flex-1"
        />
      </div>
    </section>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div
      className={cn(
        CARD_CLASS,
        'flex flex-col items-center px-6 py-16 text-center',
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-neutral-900/85 ring-1 ring-inset ring-white/[0.06]">
        <EmojiEventsIcon className="size-5 fill-neutral-500" />
      </div>
      <div className="mt-4 text-sm font-medium text-neutral-300">{title}</div>
      {description && (
        <div className="mx-auto mt-1.5 max-w-sm text-xs leading-relaxed text-neutral-500">
          {description}
        </div>
      )}
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.06] p-5">
      <div className="text-sm font-medium text-rose-200">{message}</div>
      <Button
        variant="outline"
        onClick={onRetry}
        className="mt-4 border-rose-500/30 bg-neutral-950 text-rose-100 hover:bg-neutral-900"
      >
        Retry
      </Button>
    </div>
  );
}

function QuestsSkeleton() {
  return (
    <div
      className="flex flex-col gap-8"
      role="status"
      aria-label="Loading quests"
    >
      <div className="flex flex-col gap-3">
        <div className="h-5 w-40 animate-pulse rounded bg-neutral-800" />
        <div className={CARD_CLASS}>
          <div className="divide-y divide-white/[0.06]">
            {[0, 1].map((index) => (
              <div key={index} className="flex gap-4 p-5">
                <div className="size-10 shrink-0 animate-pulse rounded-lg bg-neutral-800" />
                <div className="flex-1 space-y-2.5">
                  <div className="h-3.5 w-44 animate-pulse rounded bg-neutral-800" />
                  <div className="h-3 w-full animate-pulse rounded bg-neutral-800/70" />
                  <div className="h-3 w-28 animate-pulse rounded bg-neutral-800/70" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const dateRangeFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startValid = !Number.isNaN(startDate.getTime());
  const endValid = !Number.isNaN(endDate.getTime());

  if (!startValid && !endValid) {
    return 'Dates TBD';
  }
  if (startValid && endValid) {
    return `${dateRangeFormatter.format(startDate)} – ${dateRangeFormatter.format(endDate)}`;
  }
  return dateRangeFormatter.format(startValid ? startDate : endDate);
}
